/**
 * Reads what Home shows from the rest of the platform, for the
 * organization in the URL, as one `HomeGlance` (homeGlance.logic).
 *
 * @module components/home/useHomeGlance
 */
import { useMemo } from "react";

import { useModelSpend } from "../../state/modelSpend";
import { useActiveOrgId, useOrgs } from "../../state/orgs";
import { useRegulatoryUpdates } from "../../state/policies";
import { useWorkflows } from "../../state/workflows";
import type { FindingItem } from "../regulation/finding/findingItem";
import { buildHomeGlance, type HomeGlance } from "./homeGlance.logic";

/** `findings`: the organization's open findings. */
export function useHomeGlance(findings: ReadonlyArray<FindingItem>): HomeGlance {
  const orgId = useActiveOrgId();
  const updates = useRegulatoryUpdates(orgId).snapshot;
  const { status: spendStatus, loaded: spendLoaded } = useModelSpend(orgId);
  const workflows = useWorkflows(orgId).read;
  const me = useOrgs().viewer.userId;
  return useMemo(
    () =>
      buildHomeGlance({
        findings,
        me,
        updates,
        spend: { loaded: spendLoaded, status: spendStatus },
        workflows: workflows?.kind === "ready" ? workflows.value : [],
      }),
    [findings, me, spendLoaded, spendStatus, updates, workflows],
  );
}
